/**
 * Home Analytics — data source
 *
 * Fetches the raw statistics DTOs that feed the Home dashboard overview, using
 * the shared `Http` client + `API_ROUTES` constants (the same transport pattern
 * every feature data source follows). It returns DTOs untouched — mapping to
 * view models happens in the repository layer. Only sibling DTO *types* are
 * imported (type-only, no runtime coupling).
 */

import {Http} from '../../../../Data/config/Http';
import {API_ROUTES} from '../../../../constants/apiRoutes';
import type {BaseResponse} from '../../../../Data/types/BaseResponse';
import {
  unwrapResponse,
  handleDataSourceError
} from '../../../../utils/errorHelpers';

import type {InventoryStatisticsDTO} from '../../../inventory/models/inventory.dto';
import type {StoreStatisticsDTO} from '../../../stores/models/store.dto';
import type {UserStatisticsDTO} from '../../../users/models/user.dto';
import type {VisitStatisticsDTO} from '../../../visits/models/visit.dto';
import type {ProductsStatsDTO} from '../models/homeAnalytics.dto';

export const HomeAnalyticsAPIDataSource = {
  getInventoryStatistics: async (): Promise<InventoryStatisticsDTO> => {
    try {
      const response = await Http.get<BaseResponse<InventoryStatisticsDTO>>(
        API_ROUTES.INVENTORY.STATISTICS
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب إحصائيات المخزون');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getProductsStats: async (): Promise<ProductsStatsDTO> => {
    try {
      const response = await Http.get<BaseResponse<ProductsStatsDTO>>(
        API_ROUTES.PRODUCTS.STATS
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب إحصائيات المنتجات');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getVisitsOverview: async (): Promise<VisitStatisticsDTO> => {
    try {
      const response = await Http.get<BaseResponse<VisitStatisticsDTO>>(
        API_ROUTES.VISITS.OVERVIEW
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب تحليلات الزيارات');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getStoresStatistics: async (): Promise<StoreStatisticsDTO> => {
    try {
      const response = await Http.get<BaseResponse<StoreStatisticsDTO>>(
        API_ROUTES.STORES.STATISTICS
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب إحصائيات المتاجر');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getUsersStatistics: async (): Promise<UserStatisticsDTO> => {
    try {
      const response = await Http.get<BaseResponse<UserStatisticsDTO>>(
        API_ROUTES.USERS.STATISTICS
      );
      return unwrapResponse(
        response.data,
        'حدث خطأ في جلب إحصائيات المستخدمين'
      );
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};
